import { Box, MediaObject } from '@zillow/constellation-10';
import {
  ChatAvatar,
  ChatBubbleWithLinkFeatureToggle,
  MESSAGE_STATUS,
  MessageFooter,
  type MessageStatusVariant,
} from '@zg-rentals/satellite-components';
import { useContext } from 'react';
import stringUtils from '../../../utils/stringUtils';
import type { AttachmentsForMessage } from '../../MessageAttachments';
import MessageAttachments from '../../MessageAttachments';
import useGetFeatureFlagsAndSettings from '../../../hooks/useGetFeatureFlagsAndSettings';
import { AppContext } from '../../../contexts/AppContextProvider';
import brandUtils from '../../../utils/brandUtils';
import { useUserWarnings } from '../../../hooks/useUserWarnings';
import {
  emitOpenExternalLinkWarningModalEvent,
  emitExternalLinkClickedInExternalLinkWarningModalEvent,
} from '../../../constants/clickstreamEvents/externalLinkWarningModal';

type Props = {
  attachments: AttachmentsForMessage;
  conversationId: string;
  handleResendMessage(): void;
  isMessageOwner: boolean;
  messageContent: string;
  messageDateMs: string;
  messageId: string;
  senderName: string;
  sendStatus: MessageStatusVariant;
  shouldDisplaySentStatus: boolean;
};

const ListingMessageItem = ({
  attachments,
  conversationId,
  handleResendMessage,
  isMessageOwner,
  messageContent,
  messageDateMs,
  messageId,
  senderName,
  sendStatus,
  shouldDisplaySentStatus,
}: Props): JSX.Element => {
  const { features } = useGetFeatureFlagsAndSettings();
  const { enableMessageLink } = features;
  const { brand } = useContext(AppContext);
  const hasAttachments = attachments && !!attachments.length;
  const isMessageSent = sendStatus === MESSAGE_STATUS.SENT;
  const isMessageContentEmpty = stringUtils.isEmptyString(messageContent);
  const shouldAdjustOpacity = sendStatus === MESSAGE_STATUS.SENDING || sendStatus === MESSAGE_STATUS.FAILED;

  const { externalLinkWarningData } = useUserWarnings();

  return (
    <MediaObject
      media={!isMessageOwner && <ChatAvatar senderFullName={senderName} type="user" />}
      css={{
        alignItems: 'end',
        display: 'flex',
        flexDirection: isMessageOwner ? 'row-reverse' : 'row',
        gap: 0,
        whiteSpace: 'pre-wrap',
        width: '100%',
      }}
    >
      <Box css={{ opacity: shouldAdjustOpacity ? 0.4 : 1 }}>
        {!isMessageContentEmpty && (
          <ChatBubbleWithLinkFeatureToggle
            variant={isMessageOwner ? 'messageOwner' : 'default'}
            enableClickableLinks={!!enableMessageLink}
            externalLinkWarningData={externalLinkWarningData}
            brand={brandUtils.isHotpads(brand) ? 'hotpads' : 'zillow'}
            onImpact={brandUtils.isHotpads(brand) && isMessageOwner}
            senderName={senderName}
            shouldIncludeGutter
            onExternalLinkWarningModalOpen={emitOpenExternalLinkWarningModalEvent}
            onExternalLinkClickInModal={emitExternalLinkClickedInExternalLinkWarningModalEvent}
            enableHistoryIntegration={true}
          >
            {messageContent}
          </ChatBubbleWithLinkFeatureToggle>
        )}

        {hasAttachments && (
          <MessageAttachments
            attachments={attachments}
            conversationId={conversationId}
            isMessageOwner={isMessageOwner}
            isMessageSent={isMessageSent}
            messageId={messageId}
          />
        )}
      </Box>

      <MessageFooter
        isMessageOwner={isMessageOwner}
        messageDateMs={messageDateMs}
        resendMessage={handleResendMessage}
        sendStatus={sendStatus}
        shouldDisplaySentStatus={shouldDisplaySentStatus}
      />
    </MediaObject>
  );
};

export default ListingMessageItem;
